// src/templates/blog.js

import React from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';

/* Same construction as the /blogs listing: no width or padding of its own,
   so the global main rule supplies the page gutters that keep the column clear
   of the fixed side rails, and a 700px column inside centres it. Overriding
   main's padding here is what pushed the text off-centre and let the rails
   overlap it at narrower widths. */
const StyledBlogContainer = styled.main`
  .inner {
    max-width: 700px;
    margin: 0 auto;
  }
`;

const StyledBlogHeader = styled.header`
  margin-bottom: var(--space-7);

  /* Smaller than the display heading it replaces: at 60px the title crowded
     the meta line beneath it. */
  .blog-title {
    margin: 0;
    font-size: clamp(28px, 4vw, var(--fz-heading));;
    line-height: var(--lh-title);
    letter-spacing: -0.02em;
    color: var(--text);
  }

  /* One row, separated by space rather than punctuation. Flex with a single
     gap keeps every item on one baseline and one rhythm, which a dash and
     hardcoded nbsp could not. */

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
    margin-top: var(--space-2);
    color: var(--text-muted);
    font-size: var(--fz-md);

    /* Dot separator between items. Attaches to every child that has a
       preceding sibling, so it never leads the row. */

    > * + *::before {
      content: '·' / '';
      margin-right: var(--space-2);
      color: var(--text-disabled);
    }

    .author {
      color: var(--text-secondary);
    }

    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-2);

      /* .tags is itself a flex container, so its ::before becomes a flex
         item and the parent's gap already spaces it. Drop the margin to
         avoid doubling. */
         
      &::before {
        margin-right: 0;
      }
    }

    .tag {
      &:hover,
      &:focus-visible {
        color: var(--accent);
      }
    }
  }

  /* Where the piece was also posted. Sits directly under the date and tag
     line; only platforms with a link are rendered. */

  .release-links {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-2);

    /* Without a label the icons read as decoration rather than as "this was
       also posted here". */

    .release-label {
      color: var(--text-secondary);
      font-size: var(--fz-md);
      margin-right: var(--space-1);
    }

    a {
      display: flex;
      align-items: center;
      color: var(--text-muted);
      transition: var(--transition);

      font-size: var(--fz-md);

      &:hover,
      &:focus-visible {
        color: var(--accent);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }
`;

const StyledBlogContent = styled.div`
  margin-bottom: var(--space-4);

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2em 0 0em;
  }

  p,
  ul,
  ol,
  blockquote {
    max-width: var(--measure);
  }

  p {
    margin: 1em 0;
    line-height: var(--lh-body);
    font-size: var(--fz-lg);
    color: var(--text-secondary);
  }

  a {
    ${({ theme }) => theme.mixins.inlineLink};
  }

  code {
    background-color: var(--border);
    color: var(--text-emphasis);
    border-radius: var(--border-radius);
    font-size: var(--fz-sm);
    padding: 0.2em 0.4em;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }
`;

const BlogTemplate = ({ data, location }) => {
  const { frontmatter, html } = data.markdownRemark;
  const { title, date, tags, released } = frontmatter;
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Order is fixed so the icons stay in the same sequence across blogs;
  // a platform with no link is simply dropped.
  const releaseLinks = [
    { name: 'Linkedin', label: 'LinkedIn', url: released?.linkedin },
    { name: 'Rednote', label: 'RedNote', url: released?.rednote },
    { name: 'Twitter', label: 'X', url: released?.twitter },
    { name: 'Threads', label: 'Threads', url: released?.threads },
  ].filter(link => link.url);

  return (
    <Layout location={location}>
      <StyledBlogContainer>
        <div className="inner">
          <StyledBlogHeader>
            <Helmet title={title} />
            <span className="breadcrumb">
              <Link to="/blogs">
                <span className="arrow">&larr; Back</span>
              </Link>
            </span>
            <h1 className="blog-title">{title}</h1>
            <div className="meta">
              <span className="author">Rain Chong</span>
              <time>{formattedDate}</time>
              {tags?.length > 0 && (
                <span className="tags">
                  {tags.map((tag, i) => (
                    <Link key={i} to={`/blogs?tag=${encodeURIComponent(tag)}`} className="tag">
                    #{tag}
                    </Link>
                  ))}
                </span>
              )}
            </div>

            {releaseLinks.length > 0 && (
              <ul className="release-links">
                <li className="release-label" aria-hidden="true">
                Also on
                </li>
                {releaseLinks.map(({ name, label, url }) => (
                  <li key={name}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Read this blog on ${label}`}>
                      {name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </StyledBlogHeader>

          <StyledBlogContent dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </StyledBlogContainer>
    </Layout>
  );
};

BlogTemplate.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      html: PropTypes.string.isRequired,
      frontmatter: PropTypes.object.isRequired,
    }),
  }),
  location: PropTypes.object,
};

export default BlogTemplate;

export const pageQuery = graphql`
  query ($path: String!) {
    markdownRemark(frontmatter: { slug: { eq: $path } }) {
      html
      frontmatter {
        title
        description
        date
        slug
        tags
        released {
          linkedin
          rednote
          twitter
          threads
        }
      }
    }
  }
`;
